import { Delete } from '@mui/icons-material';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import React, { useEffect, useState } from 'react';
import { MuiDisabledTextField } from '../../../config/MaterialUIConstants';

function AddressAutocomplete({
    selectedCountry,
    triggerSelection = null,
    list,
    value,
    addressValues,
    setAddressValue,
    type,
    setCityList,
    fetchCityList,
    disable,
    loader,
    setValue = () => {},
    error,
    menuPlacement,
    clearInput = false,
}) {
    console.log('🚀 ~ AddressAutocomplete ~ type:', type, addressValues);
    const [inputValue, setInputValue] = useState('');
    const [open, setOpen] = useState(false);
    const [filteredOptions, setFilteredOptions] = useState([]);
    const [displayedOptions, setDisplayedOptions] = useState([]);
    const [itemsPerLoad] = useState(100);
    const [loadIndex, setLoadIndex] = useState(1);

    useEffect(() => {
        if (clearInput && type === 'city') {
            setInputValue('');
        }
    }, [clearInput]);

    // Filter the main list whenever input changes

    useEffect(() => {
        if (!list) {
            setFilteredOptions([]);
            setDisplayedOptions([]);
            return;
        }

        const searchTerm = inputValue.toLowerCase().trim();

        // If input is empty or too short, don't filter
        if (searchTerm.length < 2) {
            setFilteredOptions(list);
            setDisplayedOptions(list.slice(0, itemsPerLoad));
            return;
        }

        // Filter the complete list based on input
        const filtered = list.filter((option) => {
            const label = type === 'city' ? option.city?.toLowerCase() : option.province?.toLowerCase();
            return label?.includes(searchTerm);
        });

        setFilteredOptions(filtered);
        // Only display the first chunk
        setDisplayedOptions(filtered.slice(0, itemsPerLoad * loadIndex));
    }, [list, inputValue, type, itemsPerLoad, loadIndex]);

    // When loadIndex changes, update displayed options
    useEffect(() => {
        setDisplayedOptions(filteredOptions.slice(0, itemsPerLoad * loadIndex));
    }, [loadIndex, filteredOptions, itemsPerLoad]);

    useEffect(() => {
        if (triggerSelection !== null) {
            setInputValue('');
            setAddressValue({
                ...addressValues,
                countryId: selectedCountry?._id,
                cityId: '',
                provinceId: '',
                city: '',
                province: '',
            });
            setValue('province', '');
            setValue('city', '');
        }
    }, [triggerSelection]);

    const isLoading = loader;

    const handleChange = (event, newValue) => {
        if (type === 'city') {
            setAddressValue({
                ...addressValues,
                city: newValue?.city || '',
                cityId: newValue?._id || '',
                province: newValue?.province?.province || '',
                provinceId: newValue?.province?._id || '',
                countryId: newValue?.country?._id,
            });
            setValue('city', newValue?._id || '');
            setValue('province', newValue?.province?._id || '');
        } else {
            setAddressValue({
                ...addressValues,
                province: newValue?.province || '',
                provinceId: newValue?._id || '',
                city: '', // Clear city when province changes
                cityId: '', // Clear cityId when province changes
                countryId: newValue?.country?._id,
            });
            setCityList([]);
            if (newValue?._id) {
                fetchCityList(newValue._id);
            } else {
                fetchCityList();
            }
            setValue('province', newValue ? newValue._id : '');
            setValue('city', ''); // Clear city value in form
        }
    };

    const findSelectedOption = () => {
        if (!list?.length || !value) return null;
        return list.find((option) => option._id === value) || null;
    };

    const handleScroll = (event) => {
        const listboxNode = event.currentTarget;
        const bottomReached = listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 20;

        if (bottomReached && displayedOptions.length < filteredOptions.length) {
            setLoadIndex((prev) => prev + 1);
        }
    };

    // Get simple label - only show city or province name
    const getOptionLabel = (option) => {
        if (!option) return '';
        return type === 'city' ? option.city : option.province;
    };

    // Generate unique key for each option to help with duplicate management
    const getOptionKey = (option) => {
        return option._id || `${option.city || option.province}-${Math.random()}`;
    };

    return (
        <Autocomplete
            id={`${type}-autocomplete`}
            options={displayedOptions}
            getOptionLabel={getOptionLabel}
            // forcePopupIcon
            // components={{
            //     OpenPickerIcon: Delete,
            // }}
            forcePopupIcon={false}
            slots={{
                openPickerIcon: Delete,
            }}
            value={type === 'city' && !addressValues.provinceId ? null : findSelectedOption()} // Clear city value if province is cleared
            onChange={handleChange}
            disabled={disable}
            loading={isLoading}
            loadingText={
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'start',
                    }}
                >
                    Loading...
                </div>
            }
            noOptionsText={'No options found'}
            clearOnBlur={false}
            blurOnSelect
            fullWidth
            disablePortal
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            inputValue={inputValue}
            onInputChange={(event, newInputValue) => {
                setInputValue(newInputValue);
                setLoadIndex(1); // Reset chunking when input changes
            }}
            filterOptions={(x) => x} // Filtering handled manually in useEffect
            renderOption={(props, option) => (
                <li {...props} key={getOptionKey(option)}>
                    {type === 'city' ? option.city : option.province}
                </li>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    id={`${type}-textfield`}
                    variant="outlined"
                    error={error}
                    InputProps={{
                        ...params.InputProps,
                        autoComplete: 'off',
                        endAdornment: (
                            <>
                                {isLoading ? <CircularProgress size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                    sx={MuiDisabledTextField}
                />
            )}
            ListboxProps={{
                style: { maxHeight: 300, overflow: 'auto' },
                onScroll: handleScroll,
            }}
        />
    );
}

export default AddressAutocomplete;
